import React from 'react';
import styled from 'styled-components/macro';
import ScrollableAnchor from 'react-scrollable-anchor';
import {
  LinkStyled, Heading1, Heading3, Paragraph, LinkButton, Span, Heading2,
} from '../components/typography';
import { Row, Col, Container, Div } from '../components/grid';
import SidemenuLayout from '../layout/DefaultLayout/Layouts/SidemenuLayout';
import { BsClock, BsRecycle, BsTruck, GrCart, MdOutlineKitchen, TbCoins, TbStairs } from '../components/Icons';

const SubTitle = styled(Paragraph)`
  font-size: 20px;
  font-weight: bold;
  font-family: 'Inter', sans-serif;
`;

const Grid = styled.div`
  display: grid;
  grid-gap: 30px;
  grid-template-columns: 1fr 1fr 1fr;

  grid-template-areas: 
    "grid1 grid2 grid3"
    "grid4 grid5 grid6"
    "grid7 grid8 grid9";

  @media (max-width: 1199px) {
    grid-template-columns: 1fr 1fr;

    grid-template-areas: 
      "grid1 grid1"
      "grid2 grid4"
      "grid8 grid3"
      "grid5 grid6"
      "grid7 grid9";
  }

  @media (max-width: 991px) {
    grid-gap: 15px;
  }
  
  @media (max-width: 575px) {
    grid-template-columns: 1fr;

    grid-template-areas: 
      "grid1"
      "grid2"
      "grid4"
      "grid3"
      "grid5"
      "grid6"
      "grid7"
      "grid8"
      "grid9";
  }
`;

const Content = styled(Div)`
  background-color: ${({ theme }) => theme.colors.colorOffWhite};
  border-radius: 15px;
  box-shadow: rgba(99, 99, 99, 0.2) 0px 2px 8px 0px;
  padding: 30px 10px;
  text-align: center;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  grid-area: ${({ gridArea }) => gridArea};

  & > * {
    text-align: center;
  }

  & > svg {
    font-size: 40px;
    margin-bottom: 10px;
    color: ${({ theme }) => theme.colors.colorBlue};

    ${({ cart, theme }) => cart && `
      & > path {
        stroke: ${theme.colors.colorBlue};
      }
    `};
  }

  @media (max-width: 575px) {
    min-height: 180px;
  }
`;

const Title = styled(Paragraph)`
  font-size: 20px;
  font-weight: bold;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  line-height: 1;
  margin-top: 10px;
  margin-bottom: 10px;
`;

const Text = styled(Paragraph)`
  font-size: ${({ small }) => small ? '13px' : '16px'};
`;

const IconSwitch = ({ truck, clock, kitchen, recycle, coins, stairs, cart }) => {
  if (truck) return <BsTruck />;
  if (clock) return <BsClock />;
  if (kitchen) return <MdOutlineKitchen />;
  if (recycle) return <BsRecycle />;
  if (coins) return <TbCoins />;
  if (stairs) return <TbStairs />;
  if (cart) return <GrCart />;
  return null;
};

const DivBanner = styled(Div)`
  height: 300px;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

export default () => (
  <SidemenuLayout menuPicker="Leverandoer" activeSidemenu="Leverandoer">
    <Container white mb>
      <Row>
        <Col xs={12} textCenter>
          <Heading1>
            Din leverandør
          </Heading1>
          <Heading2>Af sodavand, kildevand, øl &amp; andre drikkevarer</Heading2>
          <SubTitle>Stort udvalg - nem bestilling - hurtig levering</SubTitle>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          <Grid>
            <Content gridArea="grid1">
              <IconSwitch truck />
              <Title>Gratis levering</Title>
              <Text>
                - ved bestilling af minimum 5 kasser
              </Text>
              <Text small>
                Gælder ikke fadølsanlæg &amp; kommissionsordrer
              </Text>
            </Content>
            <Content gridArea="grid2">
              <IconSwitch kitchen />
              <Title>Ind i skabet</Title>
              <Text>
                Vi leverer varerne hvor du ønsker - helt ind i skabet! Trapper er intet problem - højt som lavt.
              </Text>
            </Content>
            <Content gridArea="grid3">
              <IconSwitch clock />
              <Title>Hurtigt</Title>
              <Text>
                Bestil inden kl. 17 - levering i morgen. Mulighed for fast levering hver uge.
              </Text>
            </Content>
            <Content gridArea="grid4" bgImage="levering.leverandoer-3-v1.png" />
            <Content gridArea="grid5" cart>
              <IconSwitch cart />
              <Title>Bestil levering</Title>
              <Text>
                Bestil online eller ring til os på <Span>43 27 00 00</Span>. Hverdage: <Span>7 - 17</Span>.
              </Text>
              <Text>
                <LinkButton justifyCenter textCenter small to="/bestil/flasker-og-daaser" title="Bestil sodavand, kildevand og øl online" arrow>
                  Nem bestilling online
                </LinkButton>
              </Text>
            </Content>
            <Content gridArea="grid6" bgImage="levering.leverandoer-1.png" />
            <Content gridArea="grid7">
              <IconSwitch recycle />
              <Title>Returpant</Title>
              <Text>
                Vi tager dine tomme kasser og flasker retur
              </Text>
              <Text small>
                Også for nye kunder
              </Text>
            </Content>
            <Content gridArea="grid8" bgImage="levering.leverandoer-2.png" />
            <Content gridArea="grid9">
              <IconSwitch coins />
              <Title>Fakturering</Title>
              <Text>
                Faktura fremsendes på mail efter leveringen.
              </Text>
              <Text>
                Ean fakturering til offentlige kunder
              </Text>
            </Content>
          </Grid>
        </Col>
      </Row>
    </Container>
    <ScrollableAnchor id="info">
      <div>
        <Container white>
          <Row>
            <Col xs={12}>
              <Heading3>Kontakt os efter behov - vi kommer dag til dag</Heading3>
            </Col>
          </Row>
          <Row>
            <Col xs={12} sm={6}>
              <Paragraph>
                Vi har lager i Brøndby og dækker dagligt hele
                Storkøbenhavn og omegn.
              </Paragraph>
              <Paragraph>
                Nordsjælland helt op til Helsingør betjenes hver onsdag
              </Paragraph>
              <Paragraph>
                Modtag på mail ugentligt et bestillingslink
                til vores nemme onlinebestilling.
              </Paragraph>
              <Paragraph>
                Vores bestillingsside er klargjort med dit navn og
                kundenummer samt alle normale lagervarer og skal blot
                påføres ønsket antal.
                På siden er der endda fremhævet de varenumre,
                du plejer at bestille.
              </Paragraph>
            </Col>
            <Col xs={12} sm={6}>
              <Paragraph>
                Har du svingende - eller ikke så stort forbrug,
                kan du maile eller ringe ved behov.
                Så leverer vi den først følgende hverdag.
              </Paragraph>
              <Paragraph>
                Har du et ønske, der ikke står på listen, så spørg!
                Vi tager gerne nye øl og sodavand på programmet,
                så længe du ønsker det.
              </Paragraph>
              <Paragraph>
                Lav en <LinkStyled to="/bestil/flasker-og-daaser">online bestilling</LinkStyled>,
                ring 43 27 00 00 eller send en mail med navn og adresse,
                samt de ønskede varer.
              </Paragraph>
            </Col>
          </Row>
          <Row>
            <Col xs={12} textCenter>
              <Paragraph margin="30px 0 0 0">
                <LinkButton justifyCenter textCenter to="/bestil/flasker-og-daaser" title="Bestil sodavand, kildevand og øl online" arrow>
                  Bestil sodavand, kildevand og øl online
                </LinkButton>
              </Paragraph>
              <Paragraph fontSize="12px" textCenter marginTop="10px">
                Bare bestil selvom du er ny kunde - Vi opretter straks en konto til dig
              </Paragraph>
            </Col>
          </Row>
          <Row>
            <Col xs={12} textCenter>
              <DivBanner bgImage="levering.levering-oel-vand-lastbil-1140.jpg" />
              <Paragraph fontSize="12px" textCenter margin="0 0 10px 0">
                Distributionsbil læsser kasser med øl og sodavand af på Gammel Mønt i indre København
              </Paragraph>
            </Col>
          </Row>
        </Container>
      </div>
    </ScrollableAnchor>
  </SidemenuLayout>
);
